import { Outlet, Navigate, useLocation } from 'react-router';

import { paths } from 'src/routes/paths';

import { useAuth } from 'src/hooks/useAuth';

import { dataType } from 'src/constants';

import { SplashScreen } from 'src/components/LoadingScreen';

type ProtectedLayoutProps = {
  allowedRoles?: Array<'ADMIN' | 'PROCURER' | 'SUPPLIER'>;
};

export function ProtectedLayout({ allowedRoles }: Readonly<ProtectedLayoutProps>) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const pathname = useLocation().pathname;

  if (isLoading) {
    return <SplashScreen />;
  }

  if (!isAuthenticated || !user) {
    // Prevent infinite redirects if already on sign-in page
    if (pathname === paths.signIn) {
      return <Outlet />;
    }
    return <Navigate to={paths.signIn} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // user is logged in but doesn't have the required role
    return <Navigate to={paths.unauthorized} replace />;
  }

  if (
    user.role === dataType.userRoles.PROCURER &&
    !pathname.includes(paths.procurer.preferences.root) &&
    !pathname.includes(paths.auth.createProcurerSuccess)
  ) {
    const completedStepsArr = Object.values(dataType.procurerCompletedSteps);
    const isProcurerCompletedSetup = completedStepsArr.every((step) =>
      user.completedSteps?.includes(step)
    );
    if (!isProcurerCompletedSetup) {
      const lastCompletedStep = user.completedSteps?.[user.completedSteps.length - 1];
      const nextStepIndex =
        completedStepsArr.findIndex((step) => step === lastCompletedStep) === -1
          ? 0
          : completedStepsArr.findIndex((step) => step === lastCompletedStep) + 1;
      const nextStep = completedStepsArr[nextStepIndex];

      return <Navigate to={`${paths.procurer.preferences.root}/${nextStep}`} replace />;
    }
  }

  return <Outlet />;
}
